// Proje bazlı fiyatlandırma kontrolü (Proje Detay → İşlemler → Fiyatlandırma).
// Teklif ve sözleşme ekranları aynı kuralı buradan değerlendirir; sunucu tarafı doğrulaması
// ProjeFiyatlandirmaController.Dogrula ile aynıdır.

export interface VadeAraligi {
  baslangicGun: number;
  bitisGun: number;
  maksIndirimOrani: number;
}

export interface FiyatlandirmaKurali {
  projeID: number;
  aktifMi: boolean;
  yontem: string;
  maksOrtalamaVadeGun: number | null;
  sonOdemeTarihi: string | null;
  yoneticiKuralDisiCikabilir: boolean;
  araliklar: VadeAraligi[];
  yoneticiler: { kullaniciID: number; adSoyad: string }[];
}

export interface KontrolSonucu {
  aktif: boolean;
  muaf: boolean;
  engelli: boolean;
  ihlaller: string[];
  notlar: string[];
  ortalamaVade: number;
  aralik: VadeAraligi | null;
  minFiyat: number | null;
  gecTarihliSayisi: number;
}

export const YONTEM_ORTALAMA_VADE = 'OrtalamaVade';

export const formatTL = (n: number) => `${Math.round(n).toLocaleString('tr-TR')} ₺`;
const formatTarih = (iso: string) => { const [y, m, d] = iso.slice(0, 10).split('-'); return `${d}.${m}.${y}`; };
export const aralikEtiketi = (a: VadeAraligi) => `${a.baslangicGun}–${a.bitisGun} gün`;

export const kuralAktifMi = (kural: FiyatlandirmaKurali | null | undefined): kural is FiyatlandirmaKurali =>
  !!kural && kural.aktifMi && kural.yontem === YONTEM_ORTALAMA_VADE;

export const muafMi = (kural: FiyatlandirmaKurali | null | undefined, kullaniciID?: number | null) =>
  !!kural && kural.yoneticiKuralDisiCikabilir && kullaniciID != null && kural.yoneticiler.some(y => y.kullaniciID === kullaniciID);

export const minFiyatHesapla = (listeFiyati: number, oran: number) => Math.round(listeFiyati * (1 - oran / 100));

export const aralikBul = (kural: FiyatlandirmaKurali, vade: number): VadeAraligi | null =>
  kural.araliklar.find(a => vade >= a.baslangicGun && vade <= a.bitisGun) || null;

// Tutar ağırlıklı ortalama vade (gün), bazTarih'ten itibaren.
export const ortalamaVadeGun = (kalemler: { vadeTarihi: string; miktar: number | string }[], bazTarih: string | Date): number => {
  const baz = new Date(bazTarih);
  baz.setHours(0, 0, 0, 0);
  let agirlikli = 0;
  let toplam = 0;
  kalemler.forEach(k => {
    if (!k.vadeTarihi) return;
    const miktar = typeof k.miktar === 'number' ? k.miktar : parseFloat(k.miktar) || 0;
    const vade = new Date(k.vadeTarihi.slice(0, 10));
    vade.setHours(0, 0, 0, 0);
    const gun = Math.max(0, Math.round((vade.getTime() - baz.getTime()) / 86400000));
    agirlikli += gun * miktar;
    toplam += miktar;
  });
  return toplam > 0 ? Math.round(agirlikli / toplam) : 0;
};

// Son ödeme tarihinden sonraki girişler engellenir (muaf proje yöneticisi hariç).
export const tarihIzinliMi = (kural: FiyatlandirmaKurali | null | undefined, tarih: string, kullaniciID?: number | null) => {
  if (!kuralAktifMi(kural) || !kural.sonOdemeTarihi || !tarih || muafMi(kural, kullaniciID)) return true;
  return tarih.slice(0, 10) <= kural.sonOdemeTarihi.slice(0, 10);
};

export const sonOdemeTarihiMesaji = (kural: FiyatlandirmaKurali) =>
  `Bu projede son ödeme tarihi ${formatTarih(kural.sonOdemeTarihi!)}; daha ileri tarihli ödeme girilemez.`;

export const kontrolEt = (p: {
  kural: FiyatlandirmaKurali | null | undefined;
  listeFiyati: number | null | undefined;
  satisFiyati: number;
  ortalamaVade: number;
  vadeTarihleri: string[];
  kullaniciID?: number | null;
}): KontrolSonucu => {
  const bos: KontrolSonucu = {
    aktif: false, muaf: false, engelli: false, ihlaller: [], notlar: [],
    ortalamaVade: p.ortalamaVade, aralik: null, minFiyat: null, gecTarihliSayisi: 0,
  };
  if (!kuralAktifMi(p.kural)) return bos;
  const kural = p.kural;
  const ihlaller: string[] = [];
  const notlar: string[] = [];

  if (kural.maksOrtalamaVadeGun != null && p.ortalamaVade > kural.maksOrtalamaVadeGun) {
    ihlaller.push(`Ortalama vade ${p.ortalamaVade} gün; bu projede en fazla ${kural.maksOrtalamaVadeGun} gün olabilir.`);
  }

  let gecTarihliSayisi = 0;
  if (kural.sonOdemeTarihi) {
    const son = kural.sonOdemeTarihi.slice(0, 10);
    gecTarihliSayisi = p.vadeTarihleri.filter(t => t && t.slice(0, 10) > son).length;
    if (gecTarihliSayisi > 0) {
      ihlaller.push(`${gecTarihliSayisi} ödeme kaleminin vadesi son ödeme tarihinden (${formatTarih(son)}) sonra.`);
    }
  }

  const aralik = aralikBul(kural, p.ortalamaVade);
  let minFiyat: number | null = null;
  if (!p.listeFiyati || p.listeFiyati <= 0) {
    notlar.push('Liste fiyatı tanımlı olmadığı için fiyat kontrolü yapılamadı.');
  } else if (aralik) {
    minFiyat = minFiyatHesapla(p.listeFiyati, aralik.maksIndirimOrani);
    if (p.satisFiyati < minFiyat) {
      ihlaller.push(
        `Ortalama vade ${p.ortalamaVade} gün (${aralikEtiketi(aralik)}) için satış fiyatı en az ${formatTL(minFiyat)} olmalı ` +
        `(liste fiyatından en fazla %${aralik.maksIndirimOrani} indirim).`
      );
    }
  } else if (kural.maksOrtalamaVadeGun == null || p.ortalamaVade <= kural.maksOrtalamaVadeGun) {
    ihlaller.push(`Ortalama vade ${p.ortalamaVade} gün için tanımlı fiyat aralığı yok.`);
  }

  const muaf = muafMi(kural, p.kullaniciID);
  return {
    aktif: true, muaf, engelli: ihlaller.length > 0 && !muaf, ihlaller, notlar,
    ortalamaVade: p.ortalamaVade, aralik, minFiyat, gecTarihliSayisi,
  };
};

// Aralık düzenleyicisinin doğrulaması: belirsiz kalan değer aralıklarını kullanıcıya açıkça söyler.
export const araliklariDogrula = (araliklar: VadeAraligi[], maksVade: number | null, aktif: boolean): string[] => {
  const hatalar: string[] = [];
  if (maksVade != null && (!Number.isFinite(maksVade) || maksVade <= 0)) hatalar.push('En fazla ortalama vade 0\'dan büyük olmalıdır.');
  if (aktif && maksVade == null) hatalar.push('En fazla ortalama vade girilmelidir.');
  if (aktif && araliklar.length === 0) hatalar.push('En az bir ortalama vade aralığı ekleyin.');

  araliklar.forEach((a, i) => {
    const beklenen = i === 0 ? 0 : araliklar[i - 1].bitisGun + 1;
    if (!Number.isFinite(a.bitisGun)) {
      hatalar.push(`${i + 1}. aralığın bitiş günü girilmedi.`);
      return;
    }
    if (a.baslangicGun !== beklenen) hatalar.push(`${i + 1}. aralık ${beklenen}. günden başlamalı.`);
    if (a.bitisGun < a.baslangicGun) hatalar.push(`${a.baslangicGun}–${a.bitisGun} aralığında bitiş, başlangıçtan küçük olamaz.`);
    if (!Number.isFinite(a.maksIndirimOrani)) hatalar.push(`${aralikEtiketi(a)} aralığı için indirim oranı girilmedi.`);
    else if (a.maksIndirimOrani < 0 || a.maksIndirimOrani > 100) hatalar.push(`${aralikEtiketi(a)} aralığının indirim oranı 0 ile 100 arasında olmalı.`);
  });

  if (araliklar.length > 0 && maksVade != null && maksVade > 0) {
    const son = araliklar[araliklar.length - 1];
    if (Number.isFinite(son.bitisGun) && son.bitisGun < maksVade) {
      hatalar.push(`Son aralık ${son.bitisGun}. günde bitiyor; ${son.bitisGun + 1}–${maksVade} gün arası için indirim oranı belirsiz. Bu aralığı ekleyin.`);
    } else if (Number.isFinite(son.bitisGun) && son.bitisGun > maksVade) {
      hatalar.push(`Son aralık ${son.bitisGun}. günde bitiyor, ama en fazla ortalama vade ${maksVade} gün. Son aralığı ${maksVade}. günde bitirin.`);
    }
  }
  return hatalar;
};
